import { ReactElement } from 'react';
import { HiOutlineCheckCircle, HiXMark } from 'react-icons/hi2';
import Spinner from './Spinner';
import { useReprocess } from '../hooks/useReprocess';

/**
 * A self-contained status bar component that displays the auto-calculate status message.
 *
 * Note: reprocess no longer surfaces here. Per-run reprocess feedback is shown
 * inline by `RunProgressIndicator` (driven by `ReprocessProvider`'s `/progress`
 * poller), so the coarse app-wide reprocess banner was removed as redundant.
 *
 * @example
 * ```tsx
 * <StatusBar />
 * ```
 */
export default function StatusBar(): ReactElement | null {
  const { autoCalculateStatus, resetAutoCalculateStatus } = useReprocess();

  const getAutoCalculateStatusContent = () => {
    switch (autoCalculateStatus) {
      case 'processing':
        return {
          message: 'Auto-calibrating session... This may take a while.',
          bgColor: 'cr-bg-blue-100',
          textColor: 'cr-text-blue-800',
          icon: 'spinner',
          onClose: resetAutoCalculateStatus,
        };
      case 'success':
        return {
          message: 'Auto-calibration completed successfully!',
          bgColor: 'cr-bg-green-100',
          textColor: 'cr-text-green-800',
          icon: 'success',
          onClose: resetAutoCalculateStatus,
        };
      case 'error':
        return {
          message: 'Error during auto-calibration. Please try again.',
          bgColor: 'cr-bg-red-100',
          textColor: 'cr-text-red-800',
          icon: 'error',
          onClose: resetAutoCalculateStatus,
        };
      default:
        return null;
    }
  };

  const autoCalculateContent = getAutoCalculateStatusContent();

  // If the status is not active, don't show anything
  if (!autoCalculateContent) return null;

  const getIcon = (iconType: string) => {
    switch (iconType) {
      case 'spinner':
        return <Spinner isFetching={true} />;
      case 'success':
        return <HiOutlineCheckCircle className="cr-h-4 cr-w-4" />;
      case 'error':
        return <HiXMark className="cr-h-4 cr-w-4" />;
      case 'info':
        return <HiOutlineCheckCircle className="cr-h-4 cr-w-4" />;
      default:
        return null;
    }
  };

  const StatusBarItem = ({ content }: { content: any }) => (
    <div
      className={`cr-mt-4 cr-flex cr-items-center cr-justify-between cr-rounded-md cr-p-3 cr-text-sm
                  ${content.bgColor} ${content.textColor}`}
    >
      <div className="cr-flex cr-items-center cr-gap-2">
        {getIcon(content.icon)}
        <span>{content.message}</span>
      </div>
      <span
        onClick={content.onClose}
        className="cr-ml-2 cr-flex cr-h-5 cr-w-5 cr-flex-shrink-0 cr-cursor-pointer cr-items-center cr-justify-center cr-rounded-full hover:cr-bg-black hover:cr-bg-opacity-10"
      >
        <HiXMark className="cr-h-4 cr-w-4" />
      </span>
    </div>
  );

  return (
    <div className="cr-space-y-2">
      <StatusBarItem content={autoCalculateContent} />
    </div>
  );
}
